import Channels, { IChannel } from '@/models/Channels'

import { isChannelRestricted } from '@/helper/channel.helper'
import { ChannelRoles } from '@/models/Users'
import { ChannelsService } from '@/service'
import { reactive } from 'vue'
import { GroupState } from './groups'

export class ChannelsState {
  state = reactive({
    processing: false,
    currentChannelData: {} as any,
    hasAnnouncementRestriction: false, // to check if the current channel has announcement restriction
  })

  async fetchChannels(noCache?: boolean): Promise<void> {
    GroupState.state.channelList = undefined
    const channels = await ChannelsService.fetchAllChannels(noCache)

    GroupState.state.channelList = channels

    // Separate channels into restricted and non restricted lists
    GroupState.state.restrictedChannelList = []
    GroupState.state.nonRestrictedChannelList = []
    channels.forEach((channel: any) => {
      if (isChannelRestricted(channel)) {
        if (!GroupState.state.restrictedChannelList) {
          GroupState.state.restrictedChannelList = []
        }
        GroupState.state.restrictedChannelList.push(channel)
      } else {
        if (!GroupState.state.nonRestrictedChannelList) {
          GroupState.state.nonRestrictedChannelList = []
        }
        GroupState.state.nonRestrictedChannelList.push(channel)
      }
    })
  }
  setCurrentChannel(slug: string): any {
    if (!slug) {
      return
    }
    const channel = GroupState.state.channelList.find(
      (channel: any) => channel.slug === slug
    )
    this.state.currentChannelData = channel
    this.state.hasAnnouncementRestriction = isChannelRestricted(channel)
    return channel
  }
  async createChannel(channelData: IChannel): Promise<any> {
    const channel = new Channels(channelData)
    await channel.save()
    GroupState.state.channelList.push(channel.data)
    this.state.currentChannelData = channel
    return channel.data
  }

  async updateChannel(channelData: IChannel): Promise<void> {
    this.state.currentChannelData.name = channelData.name
    this.state.currentChannelData.description = channelData.description
    this.state.currentChannelData.icon = channelData.icon
    this.state.currentChannelData.isAnnouncement = channelData.isAnnouncement
    ChannelsService.update(this.state.currentChannelData)
  }

  async updateChannelAnnouncement(isEnabled: boolean): Promise<void> {
    this.state.currentChannelData.isAnnouncement = isEnabled
    ChannelsService.update(this.state.currentChannelData)
  }

  async deleteChannel(): Promise<void> {
    await ChannelsService.delete(this.state.currentChannelData)
    GroupState.state.channelList = GroupState.state.channelList.filter(
      (channel: any) => channel.slug !== this.state.currentChannelData.slug
    )
  }

  async fetchChannelMembers(
    paginationObject = { limit: 10, pageNo: 1 },
    roles?: Array<ChannelRoles>,
    searchText?: string,
    fetchingForMention?: boolean,
    channelData?: any
  ): Promise<any> {
    const channelMembers = await ChannelsService.fetchChannelMembers(
      channelData ? channelData._id : this.state.currentChannelData._id,
      roles?.join(','),
      paginationObject,
      searchText
    )
    if (fetchingForMention) {
      return channelMembers
    }
    if (
      paginationObject.pageNo > 1 &&
      ChannelState.state.currentChannelData.channelMembers
    )
      ChannelState.state.currentChannelData.channelMembers = [
        ...ChannelState.state.currentChannelData.channelMembers,
        ...channelMembers,
      ]
    else ChannelState.state.currentChannelData.channelMembers = channelMembers
  }
  async addChannelMembers(members: Array<any>): Promise<any> {
    const membersData = members.map(m => {
      return {
        contactId: m.contactId,
        role: ChannelRoles.MEMBER,
        notify: true,
      }
    })
    const response = await ChannelsService.addChannelMembers(
      membersData,
      this.state.currentChannelData._id
    )
    return response
  }

  async addChannelManagers(members: Array<any>): Promise<any> {
    const membersData = members.map(m => {
      return {
        contactId: m.contactId,
        role: ChannelRoles.MANAGER,
        notify: true,
      }
    })
    const response = await ChannelsService.addChannelMembers(
      membersData,
      this.state.currentChannelData._id
    )
    return response
  }

  async updateUserRoleInChannel(
    contactId: string,
    role: ChannelRoles
  ): Promise<any> {
    const response = await ChannelsService.updateMemberRole(
      this.state.currentChannelData._id,
      contactId,
      role
    )

    const member = this.state.currentChannelData.channelMembers.find(
      (m: any) => m.contactId === contactId
    )

    if (member) {
      member.role = role
    }

    if (role === ChannelRoles.MANAGER) {
      // check if the user is already exists in managers list if not add it
      const managerIndex = this.state.currentChannelData.managers.findIndex(
        (m: any) => m.contactId === contactId
      )
      if (managerIndex === -1) {
        this.state.currentChannelData.managers.push(member)
      }
    } else if (role === ChannelRoles.MEMBER) {
      const managerIndex = this.state.currentChannelData.managers.findIndex(
        (m: any) => m.contactId === contactId
      )
      if (managerIndex >= 0) {
        // remove manager from managers list
        this.state.currentChannelData.managers.splice(managerIndex, 1)
      }
    }

    return response
  }
  async removeUserFromChannel(contactId: string): Promise<any> {
    const response = await ChannelsService.removeUserFromChannel(
      this.state.currentChannelData._id,
      contactId
    )

    this.state.currentChannelData.membersCount -= 1
    // no need to fetch members all over again, simply delete the member from the channel members list
    const memberIndex = this.state.currentChannelData.channelMembers.findIndex(
      (m: any) => m.contactId === contactId
    )
    if (memberIndex > -1) {
      this.state.currentChannelData.channelMembers.splice(memberIndex, 1)
    }

    const managerIndex = this.state.currentChannelData.managers.findIndex(
      (m: any) => m.contactId === contactId
    )
    if (managerIndex > -1) {
      this.state.currentChannelData.managers.splice(managerIndex, 1)
    }

    return response
  }
  async leaveChannel(channelId: string): Promise<any> {
    const response = await ChannelsService.leaveChannel(channelId)
    return response
  }

  async updateVisibility(
    channelId: string,
    visibility = 'PRIVATE'
  ): Promise<any> {
    const response = await ChannelsService.updateChannelVisibility(
      channelId,
      visibility
    )
    return response
  }
}

export const ChannelState = new ChannelsState()
